import React from 'react';
import {useTranslation} from 'react-i18next';

import {ArrowUUpLeft, Check, MagnifyingGlass, Minus} from '../../icons';
import {
    FILTER_ALL, FILTER_BUCKET, FILTER_DOUBTS, FILTER_INCLUDED, FILTER_UNCONFIRMED,
} from '../proposalListView';

const FILTER_KEYS = [
    [FILTER_ALL, 'filter_all'],
    [FILTER_UNCONFIRMED, 'filter_unconfirmed'],
    [FILTER_INCLUDED, 'filter_included'],
    [FILTER_DOUBTS, 'filter_doubts'],
    // Va ÚLTIMO y separado del resto: los cuatro de arriba recortan la cartera y éste la deja para
    // mostrar lo que quedó afuera. Es la única forma de llegar al bloque de revisión sin scrollear
    // la lista entera — hasta acá había que saber que existía, plegado, al final de todo.
    //
    // Su rótulo nombra el CONJUNTO, no dos de sus cuatro secciones: el cajón tiene dormidas,
    // cerradas, opacas y las que el usuario sacó, y su contador las suma a las cuatro. Decía «Sin
    // movimiento o cerradas» y entonces el número no cuadraba con el título de adentro.
    [FILTER_BUCKET, 'filter_bucket'],
];

/**
 * Buscar y filtrar la propuesta cuando es grande.
 *
 * En una cartera de cientos de filas el trabajo del usuario no es leer la lista: es responder los
 * merges que esperan su gesto. Sin esta barra están repartidos entre filas que ya están resueltas y
 * no piden nada, así que la pantalla se recorre entera para encontrarlos — o no se recorre.
 *
 * Los filtros son una LENTE y nunca una decisión: mirar «sin confirmar» no destilda lo demás, y el
 * botón «Continuar» sigue contando la cartera completa (ver `proposalListView`).
 *
 * Cada botón lleva su contador para que se sepa qué hay detrás ANTES de apretarlo, y el activo lleva
 * las tres señales de la convención del producto — relleno pleno, tilde y el label completo — porque
 * un segmentado que se distingue por un gris apenas distinto no dice si el filtro está aplicado.
 */
export function ProposalToolbar({counts, filter, onFilter, query, onQuery, showLens,
                                 selection, onSelectAll, onUndo, waitForSpend = false}) {
    return (
        <div className="ob-toolbar">
            <SelectionBar selection={selection} filtered={Boolean(query) || filter !== FILTER_ALL}
                          onSelectAll={onSelectAll} onUndo={onUndo} waitForSpend={waitForSpend}/>

            {showLens && (
                <LensBar counts={counts} filter={filter} onFilter={onFilter}
                         query={query} onQuery={onQuery}/>
            )}
        </div>
    );
}

/** Buscar y filtrar. La segunda zona de la barra, la que sólo aparece cuando la lista es un muro. */
function LensBar({counts, filter, onFilter, query, onQuery}) {
    const {t} = useTranslation();

    return (
        <div className="ob-toolbar__lens">
            <div className="ob-search">
                <MagnifyingGlass size={15} className="ob-search__icon"/>
                <input type="search" value={query} className="ob-search__input"
                       placeholder={t('onboarding.clients.search_placeholder')}
                       aria-label={t('onboarding.clients.search_label')}
                       onChange={event => onQuery(event.target.value)}/>
            </div>

            <div className="ob-filters" role="group" aria-label={t('onboarding.clients.filters_label')}>
                {FILTER_KEYS.map(([key, translationKey]) => {
                    const active = filter === key;
                    const count = counts[key] || 0;
                    return (
                        <button type="button" key={key} aria-pressed={active}
                                className={'ob-filters__btn' + (active ? ' -active' : '')}
                                // Un filtro vacío se deshabilita en su lugar en vez de desaparecer:
                                // que un control aparezca y desaparezca se lee como un estado del
                                // botón. Y el activo NUNCA se deshabilita, o no habría cómo salir.
                                disabled={count === 0 && !active}
                                onClick={() => onFilter(key)}>
                            {active && <span className="ob-filters__tick" aria-hidden="true">✓</span>}
                            {t(`onboarding.clients.${translationKey}`, {count})}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * El tilde maestro de la lista.
 *
 * Existe por dos motivos y el segundo pesa más que el primero. El útil: sobre cientos de filas,
 * aceptar o limpiar de a una no es trabajo, es castigo. El decisivo: **hace visible que hay
 * checkboxes**. En la recorrida del mockup, la primera reacción a la lista fue no registrar que las
 * filas se podían tildar («ahora lo veo y me parece obvio, pero no me di cuenta»), y un control
 * maestro con la misma forma que los de las filas es lo que enseña el mecanismo sin explicarlo. Por
 * eso aparece SIEMPRE, también sobre una cartera de doce filas donde no ahorra ningún clic.
 *
 * Su alcance es la lente (`selectionOf`), y el label lo nombra: nunca dice «todas» a secas, porque
 * con un filtro puesto «todas» es una palabra que significa dos cosas distintas y sólo una es la que
 * va a pasar. Cuando lo que está por tildar incluye agrupaciones sin revisar, lo dice antes.
 */
function SelectionBar({selection, filtered, onSelectAll, onUndo, waitForSpend = false}) {
    const {t} = useTranslation();
    const {matching, ticked, merges} = selection;
    if (!matching) return null;

    const all = ticked === matching;
    const some = ticked > 0 && !all;
    // Tildar todo espera al gasto de 7 días (C-02); quitar la selección, no: deshacer nunca espera.
    const blocked = waitForSpend && !all;

    return (
        <div className="ob-select">
            <button type="button" role="checkbox" aria-checked={all ? 'true' : (some ? 'mixed' : 'false')}
                    className={`ob-check${all ? ' -on' : ''}${some ? ' -some' : ''}`}
                    onClick={() => onSelectAll(!all)} disabled={blocked}
                    aria-label={t('onboarding.clients.select_label')}>
                {all && <Check size={13} weight="bold"/>}
                {some && <Minus size={13} weight="bold"/>}
            </button>

            <button type="button" className="ob-select__action" onClick={() => onSelectAll(!all)}
                    disabled={blocked}>
                {/* Las dos direcciones nombran el alcance igual. «Quitar la selección (1)» con una
                    búsqueda puesta, al lado de un pie que dice «Continuar con 5», se lee como que
                    el botón va a dejar 1: lo que hace es quitar la de la fila que coincide. El
                    sentido de tildar ya lo decía; el de destildar no. */}
                {all
                    ? t(filtered ? 'onboarding.clients.select_none_filtered' : 'onboarding.clients.select_none',
                        {count: ticked})
                    : t(filtered ? 'onboarding.clients.select_all_filtered' : 'onboarding.clients.select_all',
                        {count: matching})}
            </button>

            {blocked ? (
                <span className="ob-select__hint -wait">{t('onboarding.clients.select_all_wait')}</span>
            ) : !all && merges > 0 && (
                <span className="ob-select__hint">{t('onboarding.clients.select_merges_hint', {count: merges})}</span>
            )}

            {onUndo && (
                <button type="button" className="ob-link ob-select__undo" onClick={onUndo}>
                    <ArrowUUpLeft size={13}/> {t('onboarding.clients.undo')}
                </button>
            )}
        </div>
    );
}

export default ProposalToolbar;
